import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Megaphone,
  Inbox,
  BarChart3,
  CheckSquare,
  UserPlus,
  ScrollText,
  Settings as SettingsIcon,
  Search,
  Bell,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/creators", label: "Creators", icon: Users },
  { to: "/app/brands", label: "Brands", icon: Building2 },
  { to: "/app/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/app/crm", label: "CRM / Leads", icon: Inbox },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/app/applications", label: "Applications", icon: UserPlus },
  { to: "/app/audit", label: "Audit Log", icon: ScrollText },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

const breadcrumbLabel = (pathname) => {
  const seg = pathname.split("/").filter(Boolean);
  if (seg.length <= 1) return "Dashboard";
  const map = {
    creators: "Creators",
    brands: "Brands",
    campaigns: "Campaigns",
    crm: "CRM / Leads",
    analytics: "Analytics",
    tasks: "Tasks",
    applications: "Applications",
    audit: "Audit Log",
    settings: "Settings",
  };
  return map[seg[1]] || "Dashboard";
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 border-r border-white/5 bg-zinc-950/95 backdrop-blur-xl transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/5">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Logo className="h-7 w-auto" testid="sidebar-brand-logo" />
          </Link>
          <button
            className="lg:hidden text-zinc-400"
            onClick={() => setOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="px-3 py-5 space-y-0.5">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-blue-600/15 text-blue-200 border border-blue-500/20"
                    : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100 border border-transparent"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 px-3">
            Workspace
          </div>
          <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
            <div className="text-sm font-medium">Bagfluence HQ</div>
            <div className="text-xs text-zinc-500">Pro plan · 8 seats</div>
          </div>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 h-16 border-b border-white/5 bg-zinc-950/85 backdrop-blur-xl flex items-center px-4 lg:px-6 gap-3">
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md text-zinc-300 hover:bg-white/5"
            aria-label="Open sidebar"
            data-testid="topbar-mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="text-sm text-zinc-500 hidden sm:flex items-center gap-2">
            <span>Bagfluence</span>
            <span className="text-zinc-700">/</span>
            <span className="text-zinc-200" data-testid="breadcrumb-current">
              {breadcrumbLabel(location.pathname)}
            </span>
          </div>

          <div className="flex-1 max-w-md ml-auto relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              placeholder="Search creators, campaigns, brands…"
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/[0.03] border border-white/8 text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/30"
              data-testid="topbar-search"
            />
          </div>

          <button
            className="relative h-9 w-9 inline-flex items-center justify-center rounded-md text-zinc-300 hover:bg-white/5"
            aria-label="Notifications"
            data-testid="topbar-notifications"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-blue-500" />
          </button>

          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-white/5"
                data-testid="topbar-user-menu"
              >
                <span className="h-7 w-7 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-xs font-semibold text-blue-200">
                  {(user?.name || "A").charAt(0)}
                </span>
                <span className="hidden md:inline text-sm text-zinc-200">
                  {user?.name || "Admin"}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
              <DropdownMenuLabel className="text-zinc-400">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem
                onClick={onLogout}
                className="text-zinc-200 focus:bg-white/5"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-5 lg:p-8 max-w-[1500px] mx-auto" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
